import { useState } from 'react';

import { downloadReportExport } from '../api/endpoints/reports';
import type { BusinessReport, ReportExportKind } from '../api/schemas';
import { formatMoneyCents } from '../lib/format';
import { changeAgainst, formatChange, periodLabel, revenueBuckets, type ReportPeriod } from '../lib/reports';
import { RevenueDaysChart } from './RevenueDaysChart';

type Direction = 'up-good' | 'down-good';

/** "+12 %" in green when the move is good for the business, red when it is not, plain when flat. */
function Change({ current, previous, direction = 'up-good' }: { current: number; previous: number; direction?: Direction }) {
  const change = changeAgainst(current, previous);
  const text = formatChange(change);
  if (!text) return <span className="report-change">sin comparación</span>;
  const favorable = change === null || text === 'igual' ? null : direction === 'up-good' ? change > 0 : change < 0;
  return (
    <span className="report-change" data-tone={favorable === null ? undefined : favorable ? 'good' : 'bad'}>
      {text}
    </span>
  );
}

export function ReportHeadline({ report }: { report: BusinessReport }) {
  const { sales, previous } = report;
  const buckets = revenueBuckets(report.days, { from: report.from, to: report.to });
  return (
    <section className="section" aria-label="Ingresos del período">
      <div className="group report-headline">
        <p className="report-headline-label">Ingresos</p>
        <p className="report-headline-value">{formatMoneyCents(sales.revenueCents)}</p>
        <p className="row-sub report-headline-compare">
          <Change current={sales.revenueCents} previous={previous.revenueCents} /> contra{' '}
          {periodLabel(report.previousPeriod)} ({formatMoneyCents(previous.revenueCents)})
        </p>
        <RevenueDaysChart buckets={buckets} />
        <dl className="report-kpis">
          <div>
            <dt>Cobros</dt>
            <dd>{sales.chargeCount}</dd>
            <Change current={sales.chargeCount} previous={previous.chargeCount} />
          </div>
          <div>
            <dt>Ticket promedio</dt>
            <dd>{formatMoneyCents(sales.averageTicketCents)}</dd>
            <Change current={sales.averageTicketCents} previous={previous.averageTicketCents} />
          </div>
          <div>
            <dt>Gastos</dt>
            <dd>{formatMoneyCents(sales.expenseCents)}</dd>
            <Change current={sales.expenseCents} previous={previous.expenseCents} direction="down-good" />
          </div>
          <div>
            <dt>Resultado</dt>
            <dd data-negative={sales.resultCents < 0 || undefined}>{formatMoneyCents(sales.resultCents)}</dd>
            <Change current={sales.resultCents} previous={previous.resultCents} />
          </div>
        </dl>
      </div>
    </section>
  );
}

const EXPORTS: readonly { kind: ReportExportKind; label: string; detail: string }[] = [
  { kind: 'cobros', label: 'Cobros', detail: 'Cada cobro con cliente, servicios, método de pago y factura' },
  { kind: 'facturas', label: 'Facturas', detail: 'Número, CUFE, RUC, base, ITBMS y total' },
  { kind: 'gastos', label: 'Gastos', detail: 'Concepto, categoría, fecha y monto' },
];

/** What the contador asks for at month end: three CSV files and a printable report. */
export function ContadorExport({ period }: { period: ReportPeriod }) {
  const [busy, setBusy] = useState<ReportExportKind | null>(null);
  const [failed, setFailed] = useState<ReportExportKind | null>(null);

  const download = async (kind: ReportExportKind) => {
    setBusy(kind);
    setFailed(null);
    try {
      await downloadReportExport(kind, period);
    } catch {
      setFailed(kind);
    } finally {
      setBusy(null);
    }
  };

  return (
    <section className="section report-section report-actions" aria-label="Para el contador">
      <header className="report-section-head">
        <h2 className="section-label">Para el contador</h2>
        <p className="row-sub">Archivos CSV de {periodLabel(period)}, abren en Excel.</p>
      </header>
      <ul className="group rows report-rows">
        {EXPORTS.map((entry) => (
          <li key={entry.kind} className="report-row">
            <span className="grow">
              <span className="report-row-label">{entry.label}</span>
              <span className="row-sub">
                {failed === entry.kind ? 'No se pudo descargar. Intenta de nuevo.' : entry.detail}
              </span>
            </span>
            <button
              type="button"
              className="btn small secondary"
              disabled={busy !== null}
              onClick={() => void download(entry.kind)}
            >
              {busy === entry.kind ? 'Preparando…' : 'Descargar'}
            </button>
          </li>
        ))}
      </ul>
      <button type="button" className="btn small ghost report-print" onClick={() => window.print()}>
        Imprimir o guardar en PDF
      </button>
    </section>
  );
}
